import { useEffect, useState } from "react";
import { Image, Plus, Trash2, Save, Eye, EyeOff, ArrowUp, ArrowDown, Film, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";

const empty = {
  eyebrow: "",
  headline: "",
  subheadline: "",
  cta_text: "Get Started",
  cta_url: "/products",
  secondary_cta_text: "",
  secondary_cta_url: "",
  image_url: "",
  video_url: "",
  overlay_opacity: 0.55,
  overlay_color: "#000000",
  text_align: "left",
  active: true,
  display_order: 0,
};

export default function HeroSlides() {
  usePageTitle("Home Hero");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null); // draft slide
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/admin/hero/slides")).data); }
    catch { toast.error("Could not load slides"); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    if (!window.confirm("Populate default hero slides? (No-op if already seeded.)")) return;
    try { await api.post("/admin/hero/slides/seed"); load(); toast.success("Seeded"); }
    catch { toast.error("Failed"); }
  };

  const save = async () => {
    if (!selected.image_url?.trim()) { toast.error("Image URL required"); return; }
    setSaving(true);
    try {
      const payload = { ...selected };
      delete payload.id; delete payload.created_at; delete payload._id;
      if (selected.id) {
        await api.put(`/admin/hero/slides/${selected.id}`, payload);
        toast.success("Slide updated");
      } else {
        await api.post("/admin/hero/slides", payload);
        toast.success("Slide created");
      }
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm("Delete this slide?")) return;
    try { await api.delete(`/admin/hero/slides/${s.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleActive = async (s) => {
    try {
      const payload = { ...s, active: !s.active };
      delete payload.id; delete payload.created_at; delete payload._id;
      await api.put(`/admin/hero/slides/${s.id}`, payload);
      load();
    } catch { toast.error("Failed"); }
  };

  const move = async (s, delta) => {
    try {
      const payload = { ...s, display_order: (s.display_order || 0) + delta };
      delete payload.id; delete payload.created_at; delete payload._id;
      await api.put(`/admin/hero/slides/${s.id}`, payload);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Image className="text-[#FF6B00]" />
          <h1 className="text-2xl font-black tracking-tight">Home Hero Slides</h1>
        </div>
        <div className="flex gap-2">
          {rows.length === 0 && <button onClick={seed} className="btn-swiss btn-outline text-xs" data-testid="hero-seed-btn">Seed Defaults</button>}
          <button onClick={() => setSelected({ ...empty, display_order: rows.length })} className="btn-swiss btn-accent text-xs" data-testid="hero-new-btn">
            <Plus size={12} /> New Slide
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {rows.map((s) => (
          <div key={s.id} className="border-2 border-zinc-200 rounded-2xl bg-white overflow-hidden flex flex-col md:flex-row" data-testid={`hero-slide-${s.id}`}>
            <div className="md:w-64 h-40 md:h-auto flex-shrink-0 relative bg-zinc-100" style={{ backgroundImage: `url("${s.image_url}")`, backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0" style={{ opacity: s.overlay_opacity || 0.55, backgroundColor: s.overlay_color || "#000000" }} />
              <div className="absolute inset-0 p-4 text-white flex flex-col justify-end">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF6B00]">{s.eyebrow || " "}</div>
                <div className="font-display text-lg font-black leading-tight whitespace-pre-line line-clamp-2">{s.headline}</div>
              </div>
            </div>
            <div className="flex-1 p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <div className="flex-1">
                <div className="text-xs text-zinc-500">
                  Order: {s.display_order || 0} · Align: {s.text_align} · Overlay: {Math.round((s.overlay_opacity || 0.55) * 100)}%
                </div>
                <div className="text-sm text-zinc-700 line-clamp-2 mt-1">{s.subheadline}</div>
                <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase font-bold">
                  {s.cta_text && <span className="bg-orange-50 text-[#FF6B00] px-2 py-0.5 rounded">CTA: {s.cta_text} → {s.cta_url}</span>}
                  {s.secondary_cta_text && <span className="bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">2nd: {s.secondary_cta_text}</span>}
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-1 items-start md:items-center">
                <button onClick={() => move(s, -1)} className="p-1.5 text-zinc-500 hover:text-[#FF6B00]" title="Move up"><ArrowUp size={14} /></button>
                <button onClick={() => move(s, 1)} className="p-1.5 text-zinc-500 hover:text-[#FF6B00]" title="Move down"><ArrowDown size={14} /></button>
                <button onClick={() => toggleActive(s)} className={`p-1.5 ${s.active ? "text-emerald-600" : "text-zinc-400"}`} title={s.active ? "Active" : "Hidden"} data-testid={`hero-toggle-${s.id}`}>
                  {s.active ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={() => setSelected({ ...s })} className="text-xs font-bold text-[#FF6B00]" data-testid={`hero-edit-${s.id}`}>Edit</button>
                <button onClick={() => remove(s)} className="text-red-500 hover:text-red-700 p-1.5" data-testid={`hero-del-${s.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="p-10 border-2 border-dashed border-zinc-200 rounded-2xl text-center text-sm text-zinc-500">
            No hero slides yet. Click "Seed Defaults" to add starter slides, or create your own.
          </div>
        )}
      </div>

      {/* Editor drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hero-editor">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-4 border-b border-zinc-100 z-10">
              <div>
                <div className="overline text-[#FF6B00]">{selected.id ? "Edit Slide" : "New Slide"}</div>
                <div className="font-display text-2xl font-black tracking-tight">Hero Slide</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelected(null)} className="btn-swiss btn-outline text-xs">Cancel</button>
                <button onClick={save} disabled={saving} className="btn-swiss btn-accent text-xs" data-testid="hero-save"><Save size={12} /> Save</button>
              </div>
            </div>

            {/* Live preview — auto-hides overlay + text when the slide has no text or CTA */}
            {(() => {
              const hasText = !!(selected.headline?.trim() || selected.eyebrow?.trim() || selected.subheadline?.trim() || selected.cta_text?.trim() || selected.secondary_cta_text?.trim());
              const overlayColor = selected.overlay_color || "#000000";
              return (
                <div className="mb-5 relative rounded-xl overflow-hidden h-40 bg-zinc-800">
                  {selected.video_url ? (
                    <video
                      key={selected.video_url}
                      src={selected.video_url}
                      autoPlay muted loop playsInline
                      poster={selected.image_url || undefined}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0" style={{ backgroundImage: `url("${selected.image_url || ""}")`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  )}
                  {hasText && (
                    <div className="absolute inset-0" style={{ opacity: selected.overlay_opacity || 0.55, backgroundColor: overlayColor }} />
                  )}
                  {hasText && (
                    <div className={`absolute inset-0 p-4 text-white flex flex-col justify-end ${selected.text_align === "center" ? "items-center text-center" : selected.text_align === "right" ? "items-end text-right" : "items-start"}`}>
                      {selected.eyebrow?.trim() && (
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF6B00]">{selected.eyebrow}</div>
                      )}
                      {selected.headline?.trim() && (
                        <div className="font-display text-2xl font-black leading-tight whitespace-pre-line">{selected.headline}</div>
                      )}
                      {selected.subheadline?.trim() && (
                        <div className="text-sm mt-1 line-clamp-2 opacity-90">{selected.subheadline}</div>
                      )}
                    </div>
                  )}
                  {!hasText && (
                    <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest font-bold text-white/80 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
                      {selected.video_url ? "Pure video slide" : "Pure image slide"}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="space-y-4">
              <Field label="Image URL*">
                <input className="input" value={selected.image_url} onChange={(e) => setSelected({ ...selected, image_url: e.target.value })} placeholder="https://images.unsplash.com/photo-..." data-testid="hero-image-url" />
                <div className="mt-2 flex items-center gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 border-2 border-zinc-300 rounded-full hover:border-[#FF6B00] hover:text-[#FF6B00]" data-testid="hero-upload-btn">
                    <Image size={12} /> Upload Image
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
                        try {
                          const form = new FormData();
                          form.append("file", file);
                          const { data } = await api.post("/admin/hero/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
                          const fullUrl = (data.url || "").startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                          setSelected({ ...selected, image_url: fullUrl });
                          toast.success("Image uploaded");
                        } catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed"); }
                        finally { e.target.value = ""; }
                      }}
                      data-testid="hero-upload-input"
                    />
                  </label>
                  <span className="text-[10px] text-zinc-500">JPEG/PNG/WEBP · max 5MB</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">💡 High-quality 1920×1080+ photos work best. Upload above or paste any URL (Unsplash, Pexels, your own).</div>
              </Field>
              <Field label="Background Video URL (optional — MP4/webm; overrides Image URL when set)">
                <input
                  className="input"
                  value={selected.video_url || ""}
                  onChange={(e) => setSelected({ ...selected, video_url: e.target.value })}
                  placeholder="https://cdn.example.com/hero.mp4"
                  data-testid="hero-video-url"
                />
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 border-2 border-zinc-300 rounded-full hover:border-[#FF6B00] hover:text-[#FF6B00]" data-testid="hero-upload-video-btn">
                    <Film size={12} /> Upload Video
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        // Client-side size guard so the admin gets an instant error instead of waiting for the 413
                        if (file.size > 30 * 1024 * 1024) {
                          toast.error("Video exceeds 30MB — compress with HandBrake and try again");
                          e.target.value = "";
                          return;
                        }
                        const form = new FormData();
                        form.append("file", file);
                        const t = toast.loading("Uploading video…");
                        try {
                          const { data } = await api.post("/admin/hero/upload-video", form, { headers: { "Content-Type": "multipart/form-data" } });
                          setSelected((prev) => ({ ...prev, video_url: data.url }));
                          toast.success(`Video uploaded (${Math.round((data.size || file.size) / 1024 / 1024 * 10) / 10} MB)`, { id: t });
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Upload failed", { id: t });
                        }
                        e.target.value = "";
                      }}
                      data-testid="hero-upload-video-input"
                    />
                  </label>
                  {selected.video_url && (
                    <button
                      type="button"
                      onClick={() => setSelected({ ...selected, video_url: "" })}
                      className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 border-2 border-zinc-200 text-zinc-500 rounded-full hover:border-red-300 hover:text-red-500"
                      data-testid="hero-clear-video-btn"
                    >
                      <X size={12} /> Clear
                    </button>
                  )}
                  <span className="text-[10px] text-zinc-500">MP4 / WebM · max 30MB</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">💡 The Image URL above becomes the poster (first-frame fallback) while the video loads. Compress to 1080p H.264 (~5-10 MB) for fastest load.</div>
              </Field>
              <Field label="Eyebrow (small text above headline)">
                <input className="input" value={selected.eyebrow || ""} onChange={(e) => setSelected({ ...selected, eyebrow: e.target.value })} placeholder="Trusted by 20,000+ businesses" data-testid="hero-eyebrow-input" />
              </Field>
              <Field label="Headline (optional — Enter for line break)">
                <textarea className="input min-h-[80px]" value={selected.headline} onChange={(e) => setSelected({ ...selected, headline: e.target.value })} placeholder="Precision.\nEvery print." data-testid="hero-headline-input" />
              </Field>
              <Field label="Sub-headline">
                <textarea className="input min-h-[70px]" value={selected.subheadline || ""} onChange={(e) => setSelected({ ...selected, subheadline: e.target.value })} data-testid="hero-sub-input" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Primary CTA Text"><input className="input" value={selected.cta_text || ""} onChange={(e) => setSelected({ ...selected, cta_text: e.target.value })} /></Field>
                <Field label="Primary CTA URL"><input className="input" value={selected.cta_url || ""} onChange={(e) => setSelected({ ...selected, cta_url: e.target.value })} placeholder="/products" /></Field>
                <Field label="Secondary CTA Text"><input className="input" value={selected.secondary_cta_text || ""} onChange={(e) => setSelected({ ...selected, secondary_cta_text: e.target.value })} /></Field>
                <Field label="Secondary CTA URL"><input className="input" value={selected.secondary_cta_url || ""} onChange={(e) => setSelected({ ...selected, secondary_cta_url: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Overlay Opacity (0–1)"><input type="number" step="0.05" min="0" max="1" className="input" value={selected.overlay_opacity} onChange={(e) => setSelected({ ...selected, overlay_opacity: parseFloat(e.target.value) || 0 })} /></Field>
                <Field label="Overlay Colour">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-10 w-14 rounded cursor-pointer border-2 border-zinc-200 bg-white p-0.5"
                      value={selected.overlay_color || "#000000"}
                      onChange={(e) => setSelected({ ...selected, overlay_color: e.target.value })}
                      data-testid="hero-overlay-color"
                    />
                    <input
                      type="text"
                      className="input flex-1 font-mono text-xs"
                      value={selected.overlay_color || "#000000"}
                      onChange={(e) => setSelected({ ...selected, overlay_color: e.target.value })}
                      placeholder="#000000"
                    />
                  </div>
                </Field>
                <Field label="Text Align">
                  <select className="input" value={selected.text_align} onChange={(e) => setSelected({ ...selected, text_align: e.target.value })}>
                    <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                  </select>
                </Field>
                <Field label="Display Order"><input type="number" className="input" value={selected.display_order || 0} onChange={(e) => setSelected({ ...selected, display_order: parseInt(e.target.value) || 0 })} /></Field>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!selected.active} onChange={(e) => setSelected({ ...selected, active: e.target.checked })} /> Active</label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
